import { useFloating, size, autoUpdate } from "@floating-ui/react";
import {
  type InputHTMLAttributes,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useOnClickOutside } from "usehooks-ts";
import { InputContainer } from "./InputContainer";
import { InputLabel } from "./Input";
import { InputText } from "./InputText";
import { InputSearchContext } from "./InputSearchContext";

export function InputSearch({
  children,
  label,
  value,
  initDropdownActive = false,
  ...inputProps
}: {
  children: ReactNode;
  value: string;
  label?: string;
  initDropdownActive?: boolean;
} & InputHTMLAttributes<HTMLInputElement>) {
  const [dropdownActive, setDropdownActive] = useState(initDropdownActive);

  const {
    refs: { setReference, setFloating },
    floatingStyles,
    update,
  } = useFloating({
    placement: "bottom-end",
    whileElementsMounted: autoUpdate,
    middleware: [
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dropdownActive) update();
  }, [dropdownActive, update]);

  useOnClickOutside(ref, () => setDropdownActive(false));

  return (
    <InputSearchContext.Provider
      value={{ value, dropdownActive, setDropdownActive }}
    >
      <div ref={ref} onFocus={() => setDropdownActive(true)}>
        <div ref={setReference}>
          {label ? <InputLabel>{label}</InputLabel> : null}
          <InputContainer>
            <InputText
              {...inputProps}
              value={value}
              autoComplete="off"
              name="searchValue"
            />
          </InputContainer>
        </div>

        {dropdownActive && (
          <ul
            ref={setFloating}
            className="z-1 overflow-y-auto rounded-sm bg-white shadow-md"
            style={floatingStyles}
          >
            {children}
          </ul>
        )}
      </div>
    </InputSearchContext.Provider>
  );
}
